"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { usePathname } from "next/navigation";
import { BreadcrumbItem } from "@utils/sharedBreadcrumbUtils";
import { generateClientBreadcrumbs } from "@utils/clientBreadcrumbUtils";

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  updateCurrentPage: (name: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([]);
  const pathname = usePathname();

  useEffect(() => {
    const updateBreadcrumbs = async () => {
      try {
        const dynamicBreadcrumbs = await generateClientBreadcrumbs(pathname);
        setBreadcrumbsState(dynamicBreadcrumbs);
      } catch (error) {
        console.error('Error generating client breadcrumbs:', error);
        const segments = pathname.split('/').filter(segment => segment);
        const fallbackBreadcrumbs = [{ name: "Home", href: "/" }];
        
        let currentPath = '';
        segments.forEach((segment, index) => {
          currentPath += `/${segment}`;
          const isLast = index === segments.length - 1;
          const name = segment.split('+').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          
          fallbackBreadcrumbs.push({
            name,
            href: currentPath,
            // current: isLast
          });
        });
        
        setBreadcrumbsState(fallbackBreadcrumbs);
      }
    };

    updateBreadcrumbs();
  }, [pathname]);

  const setBreadcrumbs = (newBreadcrumbs: BreadcrumbItem[]) => {
    setBreadcrumbsState(newBreadcrumbs);
  };

  const updateCurrentPage = (name: string) => {
    setBreadcrumbsState(prev =>
      prev.map((item, index) =>
        index === prev.length - 1
          ? { ...item, name, current: true }
          : { ...item, current: false }
      )
    );
  };

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs, updateCurrentPage }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb(): BreadcrumbContextType | any {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    console.error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
}
